import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const DAYS = [
  ['1', 'Lun'], ['2', 'Mar'], ['3', 'Mié'], ['4', 'Jue'],
  ['5', 'Vie'], ['6', 'Sáb'], ['7', 'Dom']
];

export default function UserWorkSettings({ token, isDarkMode, companyId, canManageBiometric, canManageSites }) {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('17:00');
  const [days, setDays] = useState(['1', '2', '3', '4', '5']);
  const [policy, setPolicy] = useState('Flexible');
  const [tolerance, setTolerance] = useState(15);
  const [siteIds, setSiteIds] = useState([]);
  const [biometricPolicy, setBiometricPolicy] = useState('HEREDAR');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingBiometric, setSavingBiometric] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const card = isDarkMode ? '#111827' : '#fff';
  const text = isDarkMode ? '#f8fafc' : '#1f2937';
  const muted = isDarkMode ? '#94a3b8' : '#718096';
  const border = isDarkMode ? '#334155' : '#e2e8f0';
  const input = isDarkMode ? '#0f172a' : '#fff';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        axios.get('/api/users/all-with-attendance', { headers }),
        canManageSites
          ? axios.get('/api/attendance/sites/all', { headers })
          : Promise.resolve({ data: [] })
      ]);
      const allUsers = Array.isArray(u.data) ? u.data.filter(x => x.role !== 'SuperAdministrador') : [];
      setUsers(allUsers);
      setSites(Array.isArray(s.data) ? s.data : []);
      setMessage('');
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible cargar el personal y los sitios.');
    } finally {
      setLoading(false);
    }
  }, [headers, canManageSites]);

  useEffect(() => { load(); }, [load, companyId]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(user => {
      if (!q) return true;
      return [user.name, user.email, user.id, user.role]
        .map(v => String(v ?? '').toLowerCase())
        .join(' ')
        .includes(q);
    });
  }, [users, search]);

  const loadUser = user => {
    if (!user) return;
    const workDays = String(user.work_days ?? '1,2,3,4,5').split(',').map(x => x.trim()).filter(Boolean);
    const currentSites = String(user.site_id ?? '').split(',').map(x => x.trim()).filter(x => x && x.toLowerCase() !== 'ninguno');
    setStartTime(String(user.work_start_time || '08:00'));
    setEndTime(String(user.work_end_time || '17:00'));
    setDays(workDays.length ? workDays : ['1', '2', '3', '4', '5']);
    setPolicy(String(user.policy_type || 'Flexible'));
    setTolerance(Number.isFinite(Number(user.tolerance_minutes)) ? Number(user.tolerance_minutes) : 15);
    setSiteIds(currentSites);
    setBiometricPolicy(String(user.biometricPolicyOverride || 'HEREDAR').toUpperCase());
  };

  const toggleUser = id => {
    setSelected(prev => {
      const exists = prev.includes(id);
      const next = exists ? prev.filter(x => x !== id) : [...prev, id];
      if (!exists && next.length === 1) {
        const user = users.find(x => Number(x.id) === Number(id));
        if (user) loadUser(user);
      }
      return next;
    });
  };

  const toggleAll = () => {
    const visible = filteredUsers.map(x => x.id);
    const all = visible.length > 0 && visible.every(id => selected.includes(id));
    setSelected(prev => all ? prev.filter(id => !visible.includes(id)) : Array.from(new Set([...prev, ...visible])));
  };

  const toggleDay = day => setDays(prev => prev.includes(day) ? prev.filter(x => x !== day) : [...prev, day]);

  const toggleSite = id => setSiteIds(prev => prev.includes(String(id)) ? prev.filter(x => x !== String(id)) : [...prev, String(id)]);

  const saveSchedule = async event => {
    event.preventDefault();
    if (!selected.length) return setMessage('Selecciona al menos un empleado o administrador.');
    if (!canManageSchedule) return setMessage('No tienes autorización para modificar horarios.');
    setSavingSchedule(true);
    try {
      const allIds = users.map(x => x.id);
      const targetUserId = selected.length === allIds.length ? 'Todos' : selected;
      const site_id = siteIds.length ? siteIds.join(',') : 'Ninguno';
      await axios.put('/api/attendance/settings/advanced', {
        targetUserId,
        work_start_time: startTime,
        work_end_time: endTime,
        work_days: days.join(','),
        policy_type: policy,
        tolerance_minutes: parseInt(tolerance, 10),
        site_id
      }, { headers });
      setMessage('✓ Horario, política de asistencia y geocercas aplicados correctamente.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible guardar la configuración horaria.');
    } finally {
      setSavingSchedule(false);
    }
  };

  const saveBiometric = async () => {
    if (!selected.length) return setMessage('Selecciona al menos un usuario.');
    if (!canManageBiometric) return setMessage('No tienes autorización para gestionar la biometría.');
    setSavingBiometric(true);
    try {
      for (const userId of selected) {
        await axios.put(`/api/biometric/users/${userId}/policy`, { biometricPolicyOverride: biometricPolicy }, { headers });
      }
      setMessage('✓ Política biométrica individual aplicada correctamente.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible guardar la política biométrica individual.');
    } finally {
      setSavingBiometric(false);
    }
  };

  // El menú ya exige gestionar_horarios; el backend mantiene la autorización real.
  const canManageSchedule = true;

  return (
    <section style={{ background: card, color: text, padding: 22, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 30 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>👤 Configuración individual de jornadas</h2>
        <p style={{ margin: '5px 0 0', color: muted, fontSize: 13 }}>
          Selecciona un empleado o administrador para consultar y ajustar horario, días, tolerancia, sitios/geocercas y política biométrica.
        </p>
      </div>

      {message && <div style={{ marginBottom: 14, padding: 10, borderRadius: 8, border: `1px solid ${border}`, background: isDarkMode ? '#172033' : '#f8fafc' }}>{message}</div>}

      <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔎 Buscar por nombre, correo, ID o rol..." style={{ flex: 1, minWidth: 0, padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: input, color: text }} />
          <button type="button" onClick={toggleAll} disabled={!filteredUsers.length} style={{ padding: '8px 10px', border: 0, borderRadius: 7, background: '#4a5568', color: '#fff', cursor: filteredUsers.length ? 'pointer' : 'not-allowed' }}>{filteredUsers.length && filteredUsers.every(x => selected.includes(x.id)) ? 'Quitar visibles' : 'Seleccionar visibles'}</button>
        </div>

        {loading ? <div style={{ padding: 18, textAlign: 'center', color: muted }}>Cargando personal…</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
            {filteredUsers.map(user => {
              const checked = selected.includes(user.id);
              const currentSites = String(user.site_id ?? '').split(',').filter(Boolean);
              return (
                <label key={user.id} style={{ display: 'flex', gap: 8, padding: 10, borderRadius: 8, border: `1px solid ${checked ? '#3182ce' : border}`, background: checked ? (isDarkMode ? '#1e3a8a33' : '#ebf8ff') : card, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(user.id)} style={{ marginTop: 2 }} />
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</strong>
                    <span style={{ display: 'block', fontSize: 10, color: muted, marginTop: 2 }}>ID {user.id} · {user.role}</span>
                    <span style={{ display: 'block', fontSize: 10, color: muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</span>
                    <span style={{ display: 'block', fontSize: 10, color: muted, marginTop: 4 }}>{user.work_start_time || '08:00'}–{user.work_end_time || '17:00'} · {currentSites.length ? `${currentSites.length} sitio(s)` : 'Sin geocerca'}</span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <form onSubmit={saveSchedule} style={{ display: 'grid', gap: 16 }}>
        <div style={{ padding: 14, borderRadius: 9, background: isDarkMode ? '#131924' : '#f8fafc', border: `1px solid ${border}` }}>
          <strong>{selected.length ? `${selected.length} usuario(s) seleccionado(s)` : 'Sin selección'}</strong>
          {selected.length === 1 && <span style={{ marginLeft: 8, color: muted, fontSize: 12 }}>{users.find(x => Number(x.id) === Number(selected[0]))?.name || ''}</span>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
          <div><label style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 700, color: muted }}>🕗 Hora de entrada</label><input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: input, color: text }} /></div>
          <div><label style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 700, color: muted }}>🕔 Hora de salida</label><input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: input, color: text }} /></div>
          <div><label style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 700, color: muted }}>⚙️ Política de asistencia</label><select value={policy} onChange={e => setPolicy(e.target.value)} style={{ width: '100%', padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: input, color: text }}><option value="Flexible">Flexible</option><option value="Estricto">Estricto</option></select></div>
          <div><label style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 700, color: muted }}>⏱️ Tolerancia</label><input type="number" min="0" max="180" value={tolerance} onChange={e => setTolerance(e.target.value)} style={{ width: '100%', padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: input, color: text }} /></div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 7, fontSize: 12, fontWeight: 700, color: muted }}>📅 Días laborales</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>{DAYS.map(([id, label]) => <button key={id} type="button" onClick={() => toggleDay(id)} style={{ padding: '7px 11px', borderRadius: 18, border: `1px solid ${border}`, background: days.includes(id) ? '#3182ce' : (isDarkMode ? '#1e293b' : '#edf2f7'), color: days.includes(id) ? '#fff' : text, fontWeight: 700, cursor: 'pointer' }}>{label}</button>)}</div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 7, fontSize: 12, fontWeight: 700, color: muted }}>🏢 Sitios y geocercas autorizados</label>
          {!canManageSites && <div style={{ marginBottom: 8, color: muted, fontSize: 11 }}>Solo lectura: tu permiso actual no permite modificar sitios o geocercas.</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => canManageSites && setSiteIds([])} disabled={!canManageSites} style={{ padding: '7px 11px', borderRadius: 7, border: `1px solid ${siteIds.length === 0 ? '#3182ce' : border}`, background: siteIds.length === 0 ? '#3182ce' : card, color: siteIds.length === 0 ? '#fff' : text, fontWeight: 700 }}>🗺️ Flexible (sin geocerca)</button>
            {sites.map(site => { const checked = siteIds.includes(String(site.id)); return <button key={site.id} type="button" onClick={() => canManageSites && toggleSite(site.id)} disabled={!canManageSites} style={{ padding: '7px 11px', borderRadius: 7, border: `1px solid ${checked ? '#48bb78' : border}`, background: checked ? '#48bb78' : card, color: checked ? '#fff' : text, fontWeight: 700 }}>{site.name} ({site.radius}m)</button>; })}
          </div>
        </div>

        <button type="submit" disabled={!selected.length || savingSchedule} style={{ padding: 11, border: 0, borderRadius: 7, background: !selected.length || savingSchedule ? '#718096' : '#319795', color: '#fff', fontWeight: 700, cursor: !selected.length || savingSchedule ? 'not-allowed' : 'pointer' }}>{savingSchedule ? '⏳ Guardando…' : '💾 Guardar y aplicar configuración'}</button>
      </form>

      {canManageBiometric && <div style={{ marginTop: 18, padding: 15, borderRadius: 9, border: `1px solid ${border}`, background: isDarkMode ? '#131924' : '#fffaf0' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>🔐 Política biométrica individual</h3>
        <p style={{ margin: '4px 0 12px', color: muted, fontSize: 12 }}>La configuración individual puede heredar, hacer obligatoria, hacer opcional o deshabilitar la política general de la empresa.</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}><label style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 700, color: muted }}>Configuración para los usuarios seleccionados</label><select value={biometricPolicy} onChange={e => setBiometricPolicy(e.target.value)} disabled={!selected.length || savingBiometric} style={{ width: '100%', padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: input, color: text }}><option value="HEREDAR">⚙️ Heredar configuración general</option><option value="OBLIGATORIA">🔒 Obligatoria</option><option value="OPCIONAL">🟡 Opcional</option><option value="DESHABILITADA">⚪ Deshabilitada</option></select></div>
          <button type="button" onClick={saveBiometric} disabled={!selected.length || savingBiometric} style={{ padding: '10px 14px', border: 0, borderRadius: 7, background: !selected.length || savingBiometric ? '#718096' : '#805ad5', color: '#fff', fontWeight: 700, cursor: !selected.length || savingBiometric ? 'not-allowed' : 'pointer' }}>{savingBiometric ? '⏳ Guardando…' : '💾 Aplicar política biométrica'}</button>
        </div>
      </div>}
    </section>
  );
}
